/**
 * useFinanceAttachment — o comprovante anexo de um lançamento do financeiro.
 *
 * Mesmo contrato das fotos de orçamento, e pelas mesmas razões:
 *
 * - **Bucket PRIVADO** (`quote-photos`, pasta `<uid>/finance/`). A URL guardada
 *   em `finance_entries.attachment_url` NÃO é utilizável direto: buckets antigos
 *   viraram privados e a URL crua dá 400 (armadilha #6). Toda exibição passa por
 *   `resolveMediaUrl`/`StorageImage`, que extraem o path e re-assinam.
 * - **Offline vira dataURL** e sobe no sync (`ensureStorageUrl` no executor da
 *   fila), igual às fotos. Sem isso, anexar sem rede simplesmente perderia o
 *   arquivo — em silêncio, que é o pior resultado para um comprovante.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';

export const FINANCE_ATTACHMENT_BUCKET = 'quote-photos';
export const FINANCE_ATTACHMENT_FOLDER = 'finance';

/** Teto do arquivo enviado com rede. Comprovante é foto/PDF de recibo, não álbum. */
export const FINANCE_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Teto MENOR quando não há rede: o arquivo vira dataURL e fica na fila offline,
 * que mora no localStorage (cota de poucos MB — a mesma razão de os BYTES das
 * fotos terem ido para o IndexedDB). Um comprovante de 5 MB em base64 estouraria
 * a cota e derrubaria a fila inteira, levando junto operações de outros módulos.
 */
export const FINANCE_ATTACHMENT_MAX_OFFLINE_BYTES = 1024 * 1024;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

export function useFinanceAttachment() {
  const { uploadFile } = usePhotoUpload();
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Devolve o valor a gravar em `attachment_url`: a URL do objeto (online) ou
   * um dataURL (offline, que o sync converte). `null` = nada foi anexado.
   */
  const pickAttachment = useCallback(
    async (file: File): Promise<string | null> => {
      if (file.size > FINANCE_ATTACHMENT_MAX_BYTES) {
        toast.warning('O comprovante deve ter no máximo 5 MB.');
        return null;
      }

      setIsUploading(true);
      try {
        const online = await checkNetworkStatus();
        if (!online) {
          if (file.size > FINANCE_ATTACHMENT_MAX_OFFLINE_BYTES) {
            toast.warning(
              'Sem conexão, o comprovante precisa ter no máximo 1 MB para caber na fila de sincronização.',
            );
            return null;
          }
          const dataUrl = await readAsDataUrl(file);
          toast.success('Comprovante anexado. Ele será enviado quando a conexão voltar.', {
            icon: '📴',
          });
          return dataUrl;
        }

        const result = await uploadFile(file, FINANCE_ATTACHMENT_BUCKET, FINANCE_ATTACHMENT_FOLDER);
        return result?.url ?? null;
      } catch {
        toast.error('Não foi possível anexar o comprovante.');
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFile],
  );

  return { pickAttachment, isUploading };
}
